import type { InspectionTemplateBody } from '@ayumia/contracts';
import { evaluateFormCondition, resolveProperty } from '@ayumia/component-runtime';
import { text } from '../app/i18n';

/**
 * Cibles, champs calculés et écarts d'un gabarit 1.2.0 (ADR-141 §1.3, §3 bis), côté web. Aide de
 * saisie seulement : le serveur (`readForm`, Core) relit le gabarit à sa version exacte, recalcule
 * les champs `compute`, revérifie les cibles et refuse une non-conformité obligatoire absente. Les
 * expressions passent par l'évaluateur sûr du runtime de composants, jamais par `eval` ; une
 * expression qui ne s'évalue pas ne donne ni borne ni valeur.
 */

export type Section = InspectionTemplateBody['sections'][number];
export type Entry = Section['fields'][number];
export type Answers = Record<string, unknown>;
export type Inputs = Readonly<Record<string, unknown>>;
export type OutOfTargetPolicy = 'nc_prompt' | 'nc_required' | 'wait';
/**
 * Bornes des règles de conformité citées par le formulaire, servies par GetWorkItem
 * (work-item-resolution 2.4.0, `conformity_rules`) : la même résolution que la validation au serveur.
 */
export type ConformityRules = Readonly<Record<string, { min?: number; max?: number; unit?: string }>>;

/** Même vérité que `isTruthy` du Core : faux, nul, absent et zéro sont faux. */
function truthy(value: unknown): boolean {
  return !(value === false || value === null || value === undefined || value === 0);
}

/** Valeur d'une expression sur `$answers` et `$inputs` ; `undefined` si elle ne s'évalue pas. */
export function evaluateValue(expression: unknown, answers: Answers, inputs: Inputs = {}): unknown {
  if (expression === undefined) return undefined;
  try {
    return resolveProperty({ expression }, { answers, inputs });
  } catch {
    return undefined;
  }
}

/** Condition sur `$answers` et `$inputs` (comme `nonconformity_when` au serveur) ; une erreur vaut « faux ». */
export function holds(expression: unknown, answers: Answers, inputs: Inputs = {}): boolean {
  if (expression === undefined) return true;
  return truthy(evaluateValue(expression, answers, inputs));
}

/** Visibilité d'un champ ou d'une section, sur `$answers` seulement (comme au serveur). */
export function visible(condition: unknown, answers: Answers): boolean {
  return evaluateFormCondition(condition, answers);
}

/** Valeurs d'un groupe complétées des champs calculés, dans l'ordre du gabarit (un calcul peut lire le précédent). */
export function withComputed(entries: readonly Entry[], values: Answers, inputs: Inputs = {}): Answers {
  const out: Answers = { ...values };
  for (const entry of entries) {
    if (entry.compute === undefined) continue;
    delete out[entry.field.field_id];
    const result = evaluateValue(entry.compute, out, inputs);
    if (result !== null && result !== undefined) out[entry.field.field_id] = result;
  }
  return out;
}

export interface ResolvedTarget {
  min?: number;
  max?: number;
  unit?: string;
  /** Règle partagée : ses bornes sont résolues au serveur seulement. */
  rule_ref?: string;
  policy: OutOfTargetPolicy;
}

function bound(value: unknown, answers: Answers, inputs: Inputs): number | undefined {
  if (value === undefined) return undefined;
  const result = typeof value === 'number' ? value : evaluateValue(value, answers, inputs);
  return typeof result === 'number' && Number.isFinite(result) ? result : undefined;
}

/** Slug de la règle citée : le slug, ou ce que rend son expression (1.3.0 : une règle par variante) ; `null` sinon. */
export function ruleSlugOf(ruleRef: unknown, answers: Answers, inputs: Inputs = {}): string | null {
  if (typeof ruleRef === 'string') return ruleRef;
  const value = evaluateValue(ruleRef, answers, inputs);
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Bornes d'une cible sur les réponses du groupe et les entrées de la tâche. Une règle partagée
 * (`rule_ref`) prend ses bornes dans `rules`, comme au serveur (la règle l'emporte sur les bornes
 * écrites) ; introuvable, la cible garde `rule_ref` et le serveur tranche.
 */
export function resolveTarget(entry: Entry, answers: Answers, inputs: Inputs = {}, rules: ConformityRules = {}): ResolvedTarget | undefined {
  const target = entry.target;
  if (target === undefined) return undefined;
  const out: ResolvedTarget = { policy: target.on_out_of_target ?? 'nc_prompt' };
  let min = bound(target.min, answers, inputs);
  let max = bound(target.max, answers, inputs);
  let unit = target.unit;
  if (target.rule_ref !== undefined) {
    const slug = ruleSlugOf(target.rule_ref, answers, inputs);
    const rule = slug === null ? undefined : rules[slug];
    if (rule === undefined) out.rule_ref = slug ?? JSON.stringify(target.rule_ref);
    else {
      min = rule.min ?? min;
      max = rule.max ?? max;
      unit = rule.unit ?? unit;
    }
  }
  if (min !== undefined) out.min = min;
  if (max !== undefined) out.max = max;
  if (unit !== undefined) out.unit = unit;
  return out;
}

const NUMBER = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 6 });

export function formatNumber(value: number): string {
  return NUMBER.format(value);
}

/**
 * Symboles des identifiants d'unité courants du catalogue (`unit_id`, ADR-129) : une règle partagée porte
 * l'identifiant (« celsius »), l'écran montre le symbole (« °C »). Inconnu : tel quel. Même table que la
 * tablette (`unitSymbols`, form_rules.dart).
 */
const UNIT_SYMBOLS: Readonly<Record<string, string>> = {
  celsius: '°C',
  degree_celsius: '°C',
  fahrenheit: '°F',
  kelvin: 'K',
  percent: '%',
  kilogram: 'kg',
  gram: 'g',
  milligram: 'mg',
  tonne: 't',
  litre: 'L',
  liter: 'L',
  millilitre: 'mL',
  milliliter: 'mL',
  metre: 'm',
  meter: 'm',
  centimetre: 'cm',
  centimeter: 'cm',
  millimetre: 'mm',
  millimeter: 'mm',
  second: 's',
  minute: 'min',
  hour: 'h',
  bar: 'bar',
  pascal: 'Pa',
  ph: 'pH',
};

/** Unité affichée : le symbole d'un identifiant connu, sinon l'unité telle qu'écrite. */
export function unitSymbol(unit: string): string;
export function unitSymbol(unit: string | undefined): string | undefined;
export function unitSymbol(unit: string | undefined): string | undefined {
  return unit === undefined ? undefined : (UNIT_SYMBOLS[unit.toLowerCase()] ?? unit);
}

/** « Cible 89 – 93 °C », « Cible ≥ 4 », « Cible fixée par une règle partagée » (jamais l'identifiant de la règle). */
export function formatTarget(target: ResolvedTarget): string {
  const unit = target.unit === undefined ? '' : ` ${unitSymbol(target.unit)}`;
  if (target.rule_ref !== undefined && target.min === undefined && target.max === undefined) return 'Cible fixée par une règle partagée';
  if (target.min !== undefined && target.max !== undefined) return `Cible ${formatNumber(target.min)} – ${formatNumber(target.max)}${unit}`;
  if (target.min !== undefined) return `Cible ≥ ${formatNumber(target.min)}${unit}`;
  if (target.max !== undefined) return `Cible ≤ ${formatNumber(target.max)}${unit}`;
  return 'Cible non résolue';
}

/**
 * État d'une réponse : `in` dans la cible, `out` hors cible (question ou non-conformité obligatoire),
 * `wait` « pas encore », `nonconform` réponse non conforme (`nonconformity_when`), `unknown` quand la
 * borne vit dans une règle que seul le serveur résout, `none` sans cible ni réponse.
 */
export type TargetState = 'in' | 'out' | 'wait' | 'nonconform' | 'unknown' | 'none';

export function targetState(entry: Entry, value: unknown, answers: Answers, inputs: Inputs = {}, rules: ConformityRules = {}): TargetState {
  if (value === undefined || value === null || value === '') return 'none';
  const target = resolveTarget(entry, answers, inputs, rules);
  let checked = false;
  if (target !== undefined && typeof value === 'number') {
    if (target.rule_ref !== undefined || (target.min === undefined && target.max === undefined)) return 'unknown';
    const outside = (target.min !== undefined && value < target.min) || (target.max !== undefined && value > target.max);
    if (outside) return target.policy === 'wait' ? 'wait' : 'out';
    checked = true;
  }
  if (entry.nonconformity_when !== undefined) {
    if (holds(entry.nonconformity_when, answers, inputs)) return target?.policy === 'wait' ? 'wait' : 'nonconform';
    checked = true;
  }
  return checked ? 'in' : 'none';
}

/** Un écart constaté à la validation : ce que l'opérateur peut déclarer en non-conformité. */
export interface Deviation {
  /** Chemin de la réponse, tel que le serveur l'attend dans `result.nonconformities`. */
  path: string;
  label: string;
  value: string;
  target: string;
  policy: Exclude<OutOfTargetPolicy, 'wait'>;
}

export function formatValue(entry: Entry, value: unknown, unit: string | undefined = entry.target?.unit): string {
  if (typeof value === 'number') return `${formatNumber(value)}${unit === undefined ? '' : ` ${unitSymbol(unit)}`}`;
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'string') {
    const labels = entry.field.validation?.enum_labels as Record<string, unknown> | undefined;
    const label = labels?.[value];
    return label === undefined ? value : text(label as Record<string, string> | string, 'fr', value);
  }
  return value === null || value === undefined ? '—' : JSON.stringify(value);
}

function groupDeviations(entries: readonly Entry[], raw: Answers, prefix: string, labelPrefix: string, inputs: Inputs, out: Deviation[], rules: ConformityRules): void {
  const values = withComputed(entries, raw, inputs);
  for (const entry of entries) {
    if (entry.compute === undefined && !visible(entry.visible_when, raw)) continue;
    const id = entry.field.field_id;
    const value = values[id];
    const state = targetState(entry, value, values, inputs, rules);
    if (state !== 'out' && state !== 'nonconform') continue;
    const target = resolveTarget(entry, values, inputs, rules);
    const policy = target?.policy === 'nc_required' ? 'nc_required' : 'nc_prompt';
    out.push({
      path: `${prefix}.${id}`,
      label: `${labelPrefix}${text(entry.field.label, 'fr', id)}`,
      value: formatValue(entry, value, target?.unit),
      target: state === 'out' && target !== undefined ? formatTarget(target) : 'Réponse non conforme',
      policy,
    });
  }
}

function strip(row: Answers, keys: readonly string[]): Answers {
  return Object.fromEntries(Object.entries(row).filter(([key]) => !keys.includes(key)));
}

/**
 * Écarts d'une soumission, aux mêmes chemins que le serveur (`answers.<id>`,
 * `answers.<section>.<i>.<id>`, `answers.<section>.<i>.children.<j>.<id>`). « Pas encore » n'est
 * jamais un écart ; une borne de règle partagée est laissée au serveur.
 */
export function collectDeviations(template: InspectionTemplateBody, answers: Answers, inputs: Inputs = {}, rules: ConformityRules = {}): Deviation[] {
  const out: Deviation[] = [];
  const plain: Entry[] = [];
  for (const section of template.sections) {
    if (!visible(section.visible_when, answers)) continue;
    const label = text(section.label, 'fr', section.section_id);
    const rows = Array.isArray(answers[section.section_id]) ? (answers[section.section_id] as Answers[]) : [];
    if (section.series !== undefined) {
      rows.forEach((row, index) => groupDeviations(section.fields, strip(row, ['exit']), `answers.${section.section_id}.${index}`, `${label} · N° ${index + 1} · `, inputs, out, rules));
      continue;
    }
    if (section.repeat !== undefined) {
      const children = section.repeat.children;
      const childKey = children?.child_key ?? 'child_id';
      rows.forEach((row, index) => {
        const prefix = `answers.${section.section_id}.${index}`;
        groupDeviations(section.fields, strip(row, ['item_id', 'children']), prefix, `${label} ${index + 1} · `, inputs, out, rules);
        if (children === undefined || !Array.isArray(row['children'])) return;
        (row['children'] as Answers[]).forEach((child, position) => groupDeviations(children.fields, strip(child, [childKey]), `${prefix}.children.${position}`, `${label} ${index + 1}.${position + 1} · `, inputs, out, rules));
      });
      continue;
    }
    for (const entry of section.fields) if (entry.compute !== undefined || visible(entry.visible_when, answers)) plain.push(entry);
  }
  const plainIds = new Set(plain.map((entry) => entry.field.field_id));
  const before = out.splice(0, out.length);
  groupDeviations(plain, Object.fromEntries(Object.entries(answers).filter(([key]) => plainIds.has(key))), 'answers', '', inputs, out, rules);
  out.push(...before);
  return out;
}
